import dayjs, { Dayjs } from 'dayjs'
import { useCallback, useMemo } from 'react'
import { useAppSelector } from 'store/useStoreHook'
import currencyFormat, {
    ICurrencyFormatOption,
    currencyMillifyFormat,
} from './currencyFormat'
import asPercent from 'utilities/format'

const displayPercent = (value: any) => {
    if (value == null) return ''
    return asPercent(value)
}
export function isPrefixCurrencyPosition(
    currencyCode: string,
    language: string = 'en-US',
) {
    const formatter = new Intl.NumberFormat(language, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 0,
    })

    const formatted = formatter.format(1) // e.g. "$1" or "1฿"
    return formatted.startsWith('1') ? false : true
}
export function useFormat() {
    const cCode = useAppSelector((state) => state.operation.data.currencyCode)

    const cSymbol = useAppSelector(
        (state) => state.operation.data.currencySymbol,
    )
    const userData = useAppSelector((state) => state.auth.user)
    const language = useAppSelector((state) => state.operation.data.language)
    const currentLang = useMemo(() => {
        return userData?.userPreference?.language ?? (language || 'en')
    }, [language, userData?.userPreference?.language])

    const currencyCode = useMemo(() => cCode || 'USD', [cCode])
    const currencySymbol = useMemo(() => cSymbol || '$', [cSymbol])

    const dayDateTimeShort = useCallback(
        (date?: Dayjs | string, hasOffset?: boolean) => {
            if (!date) date = dayjs()
            if (typeof date === 'string') date = dayjs(date)
            return date.format(`lll ${hasOffset ? 'Z' : ''}`)
        },
        [],
    )
    const dateImpl = useCallback(
        (date?: Dayjs | string, hasOffset?: boolean) => {
            if (!date) date = dayjs()
            if (typeof date === 'string') date = dayjs(date)
            return date.format(`llll ${hasOffset ? 'Z' : ''}`)
        },
        [],
    )
    const customDateTimeFormat = useCallback(
        (date: Dayjs | string, format?: string) => {
            if (typeof date === 'string') date = dayjs(date)
            return date.format(format || 'LLLL')
        },
        [],
    )
    const humanDateFormat = useCallback((date?: Dayjs | string) => {
        if (!date) return ''
        else if (typeof date === 'string') date = dayjs(date)
        return date.format('LL')
    }, [])

    const shortDateFormat = useCallback((date?: Dayjs | string) => {
        if (!date) return ''
        else if (typeof date === 'string') date = dayjs(date)
        return date.format('L')
    }, [])
    const formatPeriodHour = useCallback((utcDate?: string | Date) => {
        if (!utcDate) {
            return ''
        }

        const localDate = new Date(
            typeof utcDate === 'string' ? `${utcDate}Z` : utcDate,
        )

        if (isNaN(localDate.getTime())) {
            return ''
        }

        const year = localDate.getFullYear()
        const month = (localDate.getMonth() + 1).toString().padStart(2, '0')
        const day = localDate.getDate().toString().padStart(2, '0')
        const hour = localDate.getHours().toString().padStart(2, '0')

        return `${year}/${month}/${day} ${hour}:00-${hour}:59`
    }, [])
    const timeFormat = useCallback(
        (
            date?: Dayjs | string,
            includeSeconds: boolean = false,
            setMinute?: number,
        ) => {
            if (!date) return ''
            else if (typeof date === 'string') date = dayjs(date)

            if (setMinute !== undefined) {
                date = date.set('minute', setMinute)
            }

            return date.format(includeSeconds ? 'LTS' : 'LT')
        },
        [],
    )

    const timeOneHour = useCallback(
        (dateTime?: Dayjs | string, includeSeconds: boolean = false) => {
            const from = timeFormat(dateTime, includeSeconds, 0)
            const to = timeFormat(dateTime, includeSeconds, 59)

            if (!from || !to) {
                return ''
            }

            return `${from} to ${to}`
        },
        [timeFormat],
    )

    const timeFormatFromTo = useCallback(
        (
            dateTimeFrom?: Dayjs | string,
            dateTimeTo?: Dayjs | string,
            includeSeconds: boolean = false,
            setMinuteFrom?: number,
            setMinuteTo?: number,
        ) => {
            const from = timeFormat(dateTimeFrom, includeSeconds, setMinuteFrom)
            const to = timeFormat(dateTimeTo, includeSeconds, setMinuteTo)

            if (!from || !to) {
                return ''
            }

            return `${from} to ${to}`
        },
        [timeFormat],
    )

    const dateTimeImpl = useCallback(
        (date?: Dayjs | string, includeSeconds: boolean = true) => {
            if (!date) date = dayjs()
            else if (typeof date === 'string') date = dayjs(date)
            return `${shortDateFormat(date)} ${timeFormat(
                date,
                includeSeconds,
            )}`
        },
        [shortDateFormat, timeFormat],
    )
    const currencyImpl = useCallback(
        (
            value: number | string,
            otherCurrencyCode: string | undefined = undefined,
            options: ICurrencyFormatOption = {
                fraction: true,
                isAccounting: false,
            },
        ) => {
            return currencyFormat(
                value,
                currentLang,
                otherCurrencyCode || currencyCode,
                options,
            )
        },
        [currentLang, currencyCode],
    )

    const decimalFormat = useCallback(
        (
            value: number | string | undefined,
            fraction: boolean = true,
            isAccounting: boolean = false,
        ) => {
            if (!value) value = 0
            if (typeof value === 'string') value = parseFloat(value)

            return new Intl.NumberFormat(currentLang, {
                minimumFractionDigits: fraction ? 2 : undefined,
                maximumFractionDigits: fraction ? 2 : undefined,
                style: 'decimal',
                currencyDisplay: 'narrowSymbol',
                currencySign: isAccounting ? 'accounting' : 'standard',
                signDisplay: value === 0 ? 'never' : 'auto',
            }).format(value)
        },
        [currentLang],
    )

    const futureDateTimeFormat = useCallback(
        (isAsap: boolean, pickupDateTime: string | dayjs.Dayjs) => {
            const today = dayjs().startOf('day')
            const dayJsPickupDT = dayjs(pickupDateTime)
            const isToday = dayJsPickupDT.isSame(today, 'day')

            if (isAsap)
                return isToday
                    ? `Today (ASAP)`
                    : `${dayJsPickupDT.format('LL')} (ASAP)`
            else
                return isToday
                    ? `Today ${dayJsPickupDT.format('LT')}`
                    : dayJsPickupDT.format('LLLL')
        },
        [],
    )

    const inventoryQuantityFormat = useCallback(
        (value: number | undefined = 0) => {
            return new Intl.NumberFormat(currentLang, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 5,
                style: 'decimal',
                currencyDisplay: 'narrowSymbol',
                signDisplay: value === 0 ? 'never' : 'auto',
            }).format(value)
        },
        [currentLang],
    )
    const millifyCurrency = useCallback(
        (
            value: number | string,
            options: ICurrencyFormatOption = {
                fraction: true,
                isAccounting: false,
            },
        ) => {
            return currencyMillifyFormat(
                value || 0,
                currentLang,
                currencyCode,
                options,
            )
        },
        [currencyCode, currentLang],
    )

    const dayOfWeekFormat = useCallback((date?: Dayjs | string) => {
        if (!date) return ''
        else if (typeof date === 'string') date = dayjs(date)
        return date.format('dddd') // 'dddd' for full name of the day
    }, [])

    const weekRangeFormat = useCallback((date?: Dayjs | string) => {
        if (!date) return ''

        if (typeof date === 'string') date = dayjs(date)

        const startOfWeek = date.startOf('week')
        const endOfWeek = startOfWeek.add(6, 'day')

        const start = startOfWeek.format('DD')
        const end = endOfWeek.format('LL')

        return `${start}-${end}`
    }, [])

    const yearMonthFormat = useCallback(
        (date?: Dayjs | string, fullMonthName: boolean = false) => {
            if (!date) return ''

            if (typeof date === 'string') date = dayjs(date)

            return fullMonthName
                ? date.format('MMMM YYYY') // เดือนเต็มและปี
                : date.format('YYYY-MM') // ปีและเดือนในรูปแบบ YYYY-MM
        },
        [],
    )
    const timeAndDateLong = useCallback((date?: string): string => {
        const today = dayjs()
        const compareDate = dayjs(date)
        if (today.isSame(compareDate, 'day')) {
            return (
                compareDate.format('LT, ') +
                'Today, ' +
                compareDate.format('D MMMM YYYY')
            )
        }
        return compareDate.format('LT, dddd, D MMMM YYYY')
    }, [])

    return {
        date: dateImpl,
        dateTimeFormat: dateTimeImpl,
        dayDateTimeShort,
        shortDateFormat: shortDateFormat,
        humanDateFormat,
        time: timeFormat,
        currency: currencyImpl,
        currencySymbol,
        currencyCode,
        currencyLanguage: currentLang,
        decimalFormat,
        inventoryQuantityFormat,
        customDateTimeFormat,
        millifyCurrency,
        futureDateTimeFormat,
        formatPeriodHour,
        timeFormatFromTo,
        timeOneHour,
        dayOfWeekFormat,
        displayPercent,
        weekRangeFormat,
        yearMonthFormat,
        timeAndDateLong,
    }
}
export type TFormatHook = ReturnType<typeof useFormat>
